import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_LABELS, STATUS_CLASSES, STATUS_LIST, formatMinutes } from "@/lib/constants";
import { Search, Sparkles, Trash2, Eye, X, Paperclip, FileText, Loader2, Download, FileSpreadsheet, Pencil, AlertTriangle } from "lucide-react";

function AttachmentPreview({ att }) {
  const isImg = (att.content_type || "").startsWith("image/");
  const url = `${API}/files/${att.storage_path}`;
  if (isImg) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-slate-700 hover:border-amber-500 transition-colors">
        <img src={url} alt={att.original_filename} className="h-24 w-full object-cover" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border border-slate-700 hover:border-amber-500 px-3 py-3 text-sm transition-colors">
      <FileText className="h-5 w-5 text-amber-400" />
      <span className="truncate">{att.original_filename}</span>
    </a>
  );
}

const editInput = "w-full h-10 rounded-lg bg-slate-950 border border-slate-700 px-3 text-sm outline-none focus:border-amber-500";

function DetailDialog({ id, lines, parts, operators, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/assemblies/${id}`).then((r) => setData(r.data));
  useEffect(() => { if (id) { setEdit(false); setData(null); load(); } /* eslint-disable-next-line */ }, [id]);
  if (!id) return null;

  const startEdit = () => {
    setForm({
      commessa: data.commessa, line_id: data.line_id, part_id: data.part_id,
      operator_id: data.operator_id || "", date: data.date || "", due_date: data.due_date || "",
      time_minutes: data.time_minutes || 0, status: data.status, notes: data.notes || "",
    });
    setEdit(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, time_minutes: Number(form.time_minutes) || 0 };
      if (!payload.operator_id) delete payload.operator_id;
      await api.put(`/assemblies/${id}`, payload);
      toast.success("Montaggio aggiornato.");
      await load();
      setEdit(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl max-h-[90vh] overflow-auto" data-testid="detail-dialog">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-200">
          <X className="h-5 w-5" />
        </button>
        {!data ? (
          <div className="py-10 text-center text-slate-500">Caricamento…</div>
        ) : edit ? (
          <div className="space-y-3">
            <h3 className="font-heading text-xl font-bold">Modifica montaggio</h3>
            <label className="block text-xs uppercase tracking-wider text-slate-500">Commessa
              <input className={editInput + " mt-1"} data-testid="edit-commessa" value={form.commessa} onChange={(e) => set("commessa", e.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs uppercase tracking-wider text-slate-500">Linea
                <select className={editInput + " mt-1"} value={form.line_id} onChange={(e) => set("line_id", e.target.value)}>
                  {lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </label>
              <label className="block text-xs uppercase tracking-wider text-slate-500">Componente
                <select className={editInput + " mt-1"} value={form.part_id} onChange={(e) => set("part_id", e.target.value)}>
                  {parts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="block text-xs uppercase tracking-wider text-slate-500">Operatore
                <select className={editInput + " mt-1"} value={form.operator_id} onChange={(e) => set("operator_id", e.target.value)}>
                  <option value="">Non assegnato</option>
                  {operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </label>
              <label className="block text-xs uppercase tracking-wider text-slate-500">Stato
                <select className={editInput + " mt-1"} data-testid="edit-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                  {STATUS_LIST.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </label>
              <label className="block text-xs uppercase tracking-wider text-slate-500">Data
                <input type="date" className={editInput + " mt-1"} value={form.date} onChange={(e) => set("date", e.target.value)} />
              </label>
              <label className="block text-xs uppercase tracking-wider text-slate-500">Scadenza
                <input type="date" className={editInput + " mt-1"} value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
              </label>
              <label className="block text-xs uppercase tracking-wider text-slate-500">Tempo (min)
                <input type="number" min="0" data-testid="edit-time" className={editInput + " mt-1"} value={form.time_minutes} onChange={(e) => set("time_minutes", e.target.value)} />
              </label>
            </div>
            <label className="block text-xs uppercase tracking-wider text-slate-500">Note
              <textarea rows={2} className={editInput + " mt-1 h-auto py-2 resize-none"} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </label>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEdit(false)} className="flex-1 h-10 rounded-lg border border-slate-700 text-slate-300 text-sm hover:bg-slate-800">Annulla</button>
              <button onClick={save} disabled={saving} data-testid="edit-save" className="flex-1 h-10 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between pr-8">
              <div>
                <span className="font-mono-data text-amber-400 text-sm">{data.commessa}</span>
                <h3 className="font-heading text-xl font-bold mt-1">{data.part_name}</h3>
              </div>
              <button onClick={startEdit} data-testid="edit-assembly-btn" className="mt-1 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:border-amber-500 hover:text-amber-400 transition-colors">
                <Pencil className="h-3.5 w-3.5" /> Modifica
              </button>
            </div>
            <span className={`inline-block mt-2 text-xs px-2.5 py-1 rounded-full border ${STATUS_CLASSES[data.status]}`}>
              {STATUS_LABELS[data.status]}
            </span>
            <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
              <div><div className="text-slate-500 text-xs uppercase tracking-wider">Linea</div><div>{data.line_name}</div></div>
              <div><div className="text-slate-500 text-xs uppercase tracking-wider">Operatore</div><div>{data.operator_name}</div></div>
              <div><div className="text-slate-500 text-xs uppercase tracking-wider">Data</div><div className="font-mono-data">{data.date}</div></div>
              <div><div className="text-slate-500 text-xs uppercase tracking-wider">Scadenza</div><div className="font-mono-data">{data.due_date || "—"}</div></div>
            </div>
            <div className="mt-5 rounded-lg border border-slate-800 bg-slate-950/60 p-4">
              <div className="text-slate-500 text-xs uppercase tracking-wider mb-2">Tempo effettivo vs standard</div>
              <div className="flex items-center gap-6">
                <div><span className="text-slate-500 text-xs">Effettivo</span><div className="font-mono-data text-lg text-amber-400">{formatMinutes(data.time_minutes)}</div></div>
                <div><span className="text-slate-500 text-xs">Standard</span><div className="font-mono-data text-lg text-slate-300">{data.standard_minutes ? formatMinutes(data.standard_minutes) : "—"}</div></div>
                {data.over_standard && (
                  <span className="ml-auto flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/30" data-testid="over-standard-alert">
                    <AlertTriangle className="h-3.5 w-3.5" /> Fuori soglia
                  </span>
                )}
              </div>
            </div>
            {data.notes && (
              <div className="mt-5">
                <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">Note</div>
                <p className="text-sm text-slate-300 bg-slate-800/60 rounded-lg p-3">{data.notes}</p>
              </div>
            )}
            <div className="mt-5">
              <div className="text-slate-500 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" /> Allegati ({data.attachments?.length || 0})
              </div>
              {data.attachments?.length ? (
                <div className="grid grid-cols-2 gap-3">
                  {data.attachments.map((a, i) => <AttachmentPreview key={i} att={a} />)}
                </div>
              ) : (
                <p className="text-sm text-slate-600">Nessun allegato.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AutoGenDialog({ lines, assemblies, onClose, onDone }) {
  const [lineId, setLineId] = useState("");
  const [sourceCommessa, setSourceCommessa] = useState("");
  const [newCommessa, setNewCommessa] = useState("");
  const [loading, setLoading] = useState(false);

  const commesseForLine = useMemo(() => {
    const set = new Set(assemblies.filter((a) => a.line_id === lineId).map((a) => a.commessa));
    return Array.from(set);
  }, [lineId, assemblies]);

  const submit = async () => {
    if (!lineId || !newCommessa) {
      toast.error("Seleziona la linea e inserisci la nuova commessa.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/assemblies/auto-generate", {
        source_line_id: lineId,
        source_commessa: sourceCommessa || null,
        new_commessa: newCommessa,
      });
      toast.success(`Create ${data.created_count} fasi di montaggio per ${data.commessa}.`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl" data-testid="auto-generate-dialog">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-5 w-5 text-amber-400" />
          <h3 className="font-heading text-xl font-bold">Programma da storico linea</h3>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Riutilizza lo storico di una linea per generare automaticamente le fasi di montaggio della nuova commessa.
        </p>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Linea sorgente</label>
            <select data-testid="autogen-line" className="mt-2 w-full h-11 rounded-lg bg-slate-950 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500" value={lineId} onChange={(e) => { setLineId(e.target.value); setSourceCommessa(""); }}>
              <option value="">Seleziona linea…</option>
              {lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Commessa di riferimento (opzionale)</label>
            <select className="mt-2 w-full h-11 rounded-lg bg-slate-950 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500" value={sourceCommessa} onChange={(e) => setSourceCommessa(e.target.value)} disabled={!lineId}>
              <option value="">Tutte le parti della linea</option>
              {commesseForLine.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Nuova commessa</label>
            <input data-testid="autogen-new-commessa" className="mt-2 w-full h-11 rounded-lg bg-slate-950 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500" placeholder="COM-2026-XXX" value={newCommessa} onChange={(e) => setNewCommessa(e.target.value)} />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 h-11 rounded-lg border border-slate-700 text-slate-300 text-sm hover:bg-slate-800">Annulla</button>
          <button onClick={submit} disabled={loading} data-testid="autogen-submit" className="flex-1 h-11 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Genera
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Storico() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [assemblies, setAssemblies] = useState([]);
  const [lines, setLines] = useState([]);
  const [parts, setParts] = useState([]);
  const [operators, setOperators] = useState([]);
  const [q, setQ] = useState("");
  const [lineFilter, setLineFilter] = useState("");
  const [commessaFilter, setCommessaFilter] = useState(searchParams.get("commessa") || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [showAutoGen, setShowAutoGen] = useState(false);

  const load = async () => {
    const [a, l, p, o] = await Promise.all([
      api.get("/assemblies"), api.get("/lines"), api.get("/parts"), api.get("/operators"),
    ]);
    setAssemblies(a.data);
    setLines(l.data);
    setParts(p.data);
    setOperators(o.data);
  };
  useEffect(() => { load(); }, []);

  const commesse = useMemo(
    () => Array.from(new Set(assemblies.map((a) => a.commessa))).sort(),
    [assemblies]
  );

  const exportData = async (fmt) => {
    const params = new URLSearchParams({ format: fmt });
    if (lineFilter) params.append("line_id", lineFilter);
    if (commessaFilter) params.append("commessa", commessaFilter);
    if (statusFilter) params.append("status", statusFilter);
    try {
      const { data } = await api.get(`/export/assemblies?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "pdf" ? "storico_montaggi.pdf" : "storico_montaggi.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Esportazione avviata.");
    } catch (e) {
      toast.error("Errore durante l'esportazione.");
    }
  };

  const filtered = useMemo(() => {
    return assemblies.filter((a) => {
      if (lineFilter && a.line_id !== lineFilter) return false;
      if (commessaFilter && a.commessa !== commessaFilter) return false;
      if (statusFilter && a.status !== statusFilter) return false;
      if (q) {
        const t = `${a.commessa} ${a.part_name} ${a.part_code} ${a.line_name} ${a.operator_name}`.toLowerCase();
        if (!t.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [assemblies, q, lineFilter, commessaFilter, statusFilter]);

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo montaggio?")) return;
    await api.delete(`/assemblies/${id}`);
    toast.success("Montaggio eliminato.");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Storico Montaggi</h1>
          <p className="text-slate-500 text-sm mt-1">{filtered.length} montaggi trovati.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => exportData("xlsx")} data-testid="export-excel-btn" className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm flex items-center gap-2 transition-colors">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          <button onClick={() => exportData("pdf")} data-testid="export-pdf-btn" className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-semibold text-sm flex items-center gap-2 transition-colors">
            <Download className="h-4 w-4" /> PDF
          </button>
          <button onClick={() => setShowAutoGen(true)} data-testid="btn-auto-generate-order" className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm flex items-center gap-2 transition-colors">
            <Sparkles className="h-4 w-4" /> Programma da storico
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input data-testid="input-search-history" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca commessa, parte, operatore…" className="w-full h-11 rounded-lg bg-slate-900 border border-slate-700 pl-10 pr-4 text-sm outline-none focus:border-amber-500 transition-colors" />
        </div>
        <select data-testid="filter-line-select" value={lineFilter} onChange={(e) => setLineFilter(e.target.value)} className="h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500">
          <option value="">Tutte le linee</option>
          {lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select data-testid="filter-commessa-select" value={commessaFilter} onChange={(e) => setCommessaFilter(e.target.value)} className="h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500">
          <option value="">Tutte le commesse</option>
          {commesse.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="filter-status-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500">
          <option value="">Tutti gli stati</option>
          {STATUS_LIST.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="assembly-table">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs font-mono-data uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Commessa</th>
                <th className="px-4 py-3">Componente</th>
                <th className="px-4 py-3 hidden md:table-cell">Linea</th>
                <th className="px-4 py-3 hidden lg:table-cell">Operatore</th>
                <th className="px-4 py-3 hidden sm:table-cell">Data</th>
                <th className="px-4 py-3">Tempo</th>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} data-testid={`assembly-row-${a.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors">
                  <td className="px-4 py-3 font-mono-data text-amber-400">{a.commessa}</td>
                  <td className="px-4 py-3">
                    {a.part_name}
                    {a.attachments?.length > 0 && <Paperclip className="inline h-3.5 w-3.5 ml-1.5 text-slate-500" />}
                    {a.over_standard && <AlertTriangle className="inline h-3.5 w-3.5 ml-1.5 text-rose-400" />}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-400">{a.line_name}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-400">{a.operator_name}</td>
                  <td className="px-4 py-3 hidden sm:table-cell font-mono-data text-slate-400">{a.date}</td>
                  <td className="px-4 py-3 font-mono-data">{formatMinutes(a.time_minutes)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2.5 py-1 rounded-full border whitespace-nowrap ${STATUS_CLASSES[a.status]}`}>
                      {STATUS_LABELS[a.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setDetailId(a.id)} data-testid={`view-${a.id}`} className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800" title="Dettagli">
                        <Eye className="h-4 w-4" />
                      </button>
                      {user?.role === "admin" && (
                        <button onClick={() => remove(a.id)} data-testid={`delete-${a.id}`} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800" title="Elimina">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">Nessun montaggio trovato.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DetailDialog id={detailId} lines={lines} parts={parts} operators={operators} onClose={() => setDetailId(null)} onChanged={load} />
      {showAutoGen && (
        <AutoGenDialog lines={lines} assemblies={assemblies} onClose={() => setShowAutoGen(false)} onDone={() => { setShowAutoGen(false); load(); }} />
      )}
    </div>
  );
}
